import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertTriangle, Calendar, X, BarChart3, Users } from 'lucide-react';

const MiniMatrixPreview = ({ isActive = true }) => {
  const [visibleRows, setVisibleRows] = useState(0);
  const [visibleCells, setVisibleCells] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);

  const requirements = ['First Aid', 'Fire Safety', 'GDPR', 'H&S'];
  const team = [
    { name: 'John S.', statuses: ['valid', 'expired', 'soon', 'valid'] },
    { name: 'Sarah J.', statuses: ['valid', 'valid', 'missing', 'soon'] },
    { name: 'Mike W.', statuses: ['soon', 'valid', 'valid', 'expired'] },
    { name: 'Emma D.', statuses: ['valid', 'booked', 'valid', 'valid'] },
    { name: 'Alex R.', statuses: ['missing', 'valid', 'expired', 'booked'] },
    { name: 'Lisa T.', statuses: ['valid', 'soon', 'valid', 'valid'] },
    { name: 'Tom B.', statuses: ['booked', 'missing', 'valid', 'soon'] }
  ];

  // Start animation when component becomes active
  useEffect(() => {
    if (isActive && !isAnimating) {
      setIsAnimating(true);
    } else if (!isActive && isAnimating) {
      // Reset when becoming inactive
      setIsAnimating(false);
      setVisibleRows(0);
      setVisibleCells({});
    }
  }, [isActive]);

  useEffect(() => {
    if (!isAnimating) return;

    // Progressive row reveal
    const rowTimer = setInterval(() => {
      setVisibleRows(prev => {
        if (prev >= team.length) {
          clearInterval(rowTimer);
          // After all rows are visible, wait then restart
          setTimeout(() => {
            setVisibleRows(0);
            setVisibleCells({});
            setIsAnimating(false);
            setTimeout(() => setIsAnimating(true), 100);
          }, 8000); // Show complete matrix for 8 seconds
          return prev;
        }
        return prev + 1;
      });
    }, 600); // Each row appears after 600ms

    return () => clearInterval(rowTimer);
  }, [isAnimating, team.length]);

  // Animate cells for each row as it becomes visible
  useEffect(() => {
    if (visibleRows > 0 && visibleRows <= team.length) {
      const currentRow = visibleRows - 1;

      // Set all cells for this row with delays
      requirements.forEach((_, cellIndex) => {
        setTimeout(() => {
          setVisibleCells(prev => ({
            ...prev,
            [`${currentRow}-${cellIndex}`]: true
          }));
        }, cellIndex * 100); // Stagger each cell by 100ms
      });
    }
  }, [visibleRows, requirements.length, team.length]);

  const statusConfig = {
    valid: {
      icon: Check,
      bgClass: 'bg-green-50/60 border-green-200/20 hover:bg-green-50/80 hover:border-green-200/40',
      iconColor: 'text-green-500',
      label: 'Valid'
    },
    soon: {
      icon: AlertTriangle,
      bgClass: 'bg-amber-50/60 border-amber-200/20 hover:bg-amber-50/80 hover:border-amber-200/40',
      iconColor: 'text-amber-500',
      label: 'Expiring'
    },
    expired: {
      icon: AlertTriangle,
      bgClass: 'bg-red-50/60 border-red-200/20 hover:bg-red-50/80 hover:border-red-200/40',
      iconColor: 'text-red-500',
      label: 'Expired'
    },
    missing: {
      icon: X,
      bgClass: 'bg-red-50/60 border-red-200/20 hover:bg-red-50/80 hover:border-red-200/40',
      iconColor: 'text-red-500',
      label: 'Missing'
    },
    booked: {
      icon: Calendar,
      bgClass: 'bg-blue-50/60 border-blue-200/20 hover:bg-blue-50/80 hover:border-blue-200/40',
      iconColor: 'text-blue-500',
      label: 'Booked'
    }
  };

  // Calculate gap analysis stats
  const stats = {
    compliant: 0,
    expiring: 0,
    expired: 0,
    missing: 0,
    booked: 0
  };

  team.forEach(member => {
    member.statuses.forEach(status => {
      if (status === 'valid') stats.compliant++;
      else if (status === 'soon') stats.expiring++;
      else if (status === 'expired') stats.expired++;
      else if (status === 'missing') stats.missing++;
      else if (status === 'booked') stats.booked++;
    });
  });

  const totalCells = team.length * requirements.length;
  const complianceRate = Math.round((stats.compliant / totalCells) * 100);

  return (
    <div className="w-full h-full p-8">
      {/* Main Matrix Container */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex h-full">
        {/* Matrix Content */}
        <div className="flex-1 p-6">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Skills Matrix</h3>
              <p className="text-xs text-gray-500">Live compliance tracking</p>
            </div>
            <div className="text-sm text-green-600 font-semibold">
              {complianceRate}% Compliant
            </div>
          </div>

          {/* Matrix Grid */}
          <div className="overflow-hidden">
            <table className="w-full border-separate" style={{ tableLayout: 'fixed', borderSpacing: '2px' }}>
              <thead>
                <tr>
                  <th className="text-left pb-3 pl-2 w-14">
                    <Users className="w-4 h-4 text-gray-400" />
                  </th>
                  {requirements.map((req, i) => (
                    <th key={i} className="text-center pb-3 text-xs font-medium text-gray-600">
                      {req}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                {team.map((member, rowIndex) => (
                  <motion.tr
                    key={rowIndex}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{
                      opacity: rowIndex < visibleRows ? 1 : 0,
                      x: rowIndex < visibleRows ? 0 : -50
                    }}
                    transition={{
                      duration: 0.5,
                      ease: "easeOut"
                    }}
                  >
                    <td className="py-1 pl-2">
                      <div className="text-[11px] font-medium text-gray-700">
                        {member.name}
                      </div>
                    </td>
                    {member.statuses.map((status, colIndex) => {
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const isVisible = visibleCells[cellKey];
                      const StatusIcon = statusConfig[status].icon;

                      return (
                        <td key={colIndex} className="border-0 p-0.5">
                          <AnimatePresence>
                            {isVisible && (
                              <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 500,
                                  damping: 25,
                                  duration: 0.3
                                }}
                                className={`
                                  h-10 w-full rounded-lg relative overflow-hidden group
                                  transition-all duration-400 ease-out border
                                  ${statusConfig[status].bgClass}
                                  hover:shadow-md cursor-pointer
                                `}
                              >
                                <div className="h-full flex flex-col items-center justify-center">
                                  <StatusIcon
                                    className={`h-3 w-3 mb-0.5 ${statusConfig[status].iconColor}`}
                                    strokeWidth={1.5}
                                  />
                                  <span className="text-[9px] font-normal text-gray-600">
                                    {status === 'soon' ? 'Soon' : status === 'missing' ? 'Missing' : status === 'booked' ? 'Booked' : ''}
                                  </span>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </td>
                      );
                    })}
                  </motion.tr>
                ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>

        {/* Gap Analysis Sidebar */}
        <div className="w-24 bg-gray-50/50 border-l border-gray-200 p-4">
          {/* Header */}
          <div className="mb-4 text-center">
            <BarChart3 className="w-4 h-4 mx-auto text-gray-600 mb-1" />
            <p className="text-xs font-semibold text-gray-700">Gap</p>
          </div>

          {/* Stats */}
          <div className="space-y-3">
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: visibleRows > 2 ? 1 : 0, x: visibleRows > 2 ? 0 : 10 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-xl font-bold text-green-600">{stats.compliant}</div>
              <div className="text-xs text-gray-600">Valid</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: visibleRows > 3 ? 1 : 0, x: visibleRows > 3 ? 0 : 10 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-xl font-bold text-amber-500">{stats.expiring}</div>
              <div className="text-xs text-gray-600">Soon</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: visibleRows > 4 ? 1 : 0, x: visibleRows > 4 ? 0 : 10 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-xl font-bold text-red-500">{stats.expired}</div>
              <div className="text-xs text-gray-600">Expired</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: visibleRows > 5 ? 1 : 0, x: visibleRows > 5 ? 0 : 10 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-xl font-bold text-blue-500">{stats.booked}</div>
              <div className="text-xs text-gray-600">Booked</div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniMatrixPreview;